import React from 'react';
import { Contact } from '../../../config/api';
import { highlightText } from '../../../utils/textHighlight';

interface ContactCardProps {
  contact: Contact;
  onClick: () => void;
  searchQuery?: string; // Add searchQuery prop
}

const ContactCard: React.FC<ContactCardProps> = ({ contact, onClick, searchQuery = '' }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'lead':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'qualified':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'converted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  const getContactTypeIcon = (type: string) => {
    return type === 'company' ? '🏢' : '👤';
  };

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-lex-accent transition-all duration-200 cursor-pointer"
    >
      {/* Header */}
      <div className="mb-4">
        {/* Top row with icon, contact type, and status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="text-2xl">{getContactTypeIcon(contact.contact_type)}</div>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
              {contact.contact_type}
            </p>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}>
            {contact.status}
          </span>
        </div>
        
        {/* Contact name on its own line */}
        <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight break-words">
          {highlightText(contact.name, searchQuery)}
        </h3>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        {contact.email && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
            <span className="mr-2">📧</span>
            <span className="truncate">{highlightText(contact.email, searchQuery)}</span>
          </div>
        )}
        
        {contact.phone && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
            <span className="mr-2">📞</span>
            <span>{highlightText(contact.phone, searchQuery)}</span>
          </div>
        )}

        {/* Notes Preview */}
        {contact.notes && (
          <div className="flex items-start text-sm text-gray-600 dark:text-gray-300">
            <span className="mr-2 mt-0.5">📝</span>
            <span className="line-clamp-2 text-xs">
              {contact.notes.replace(/<[^>]*>/g, '').substring(0, 100)}
              {contact.notes.length > 100 && '...'}
            </span>
          </div>
        )}
      </div>

      {/* Tags */}
      {contact.tags && contact.tags.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {contact.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
              >
                {tag}
              </span>
            ))}
            {contact.tags.length > 3 && (
              <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                +{contact.tags.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Created {new Date(contact.created_at).toLocaleDateString()}</span>
        {contact.last_contact_date && (
          <span>Last contact {new Date(contact.last_contact_date).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );
};

export default ContactCard; 